import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, useUnit, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { canCreateTickets } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Search, 
  Filter,
  Plus,
  TicketIcon,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const EQUIPMENT_CATEGORIES = [
  "Kitchen Equipment", "HVAC", "Plumbing", "Electrical", 
  "Lighting", "Upholstery", "Bathroom", "Structural", "Other"
];

const STATUS_OPTIONS = [
  "Troubleshooting in Progress",
  "Resolved by Manager",
  "Escalated to Help Desk",
  "Assigned to Technician",
  "Vendor Assigned",
  "Pending Cost Approval",
  "Repair Authorized",
  "Resolved",
];

const STATUS_COLORS = {
  "Troubleshooting in Progress": { bg: "#DBEAFE", text: "#1E40AF" },
  "Escalated to Help Desk": { bg: "#DBEAFE", text: "#1E40AF" },
  "Assigned to Technician": { bg: "#FEF9C3", text: "#854D0E" },
  "Vendor Assigned": { bg: "#FEF9C3", text: "#854D0E" },
  "Pending Cost Approval": { bg: "#FFEDD5", text: "#9A3412" },
  "Repair Authorized": { bg: "#EEF2FF", text: "#4338CA" },
  "Resolved": { bg: "#DCFCE7", text: "#166534" },
  "Resolved by Manager": { bg: "#DCFCE7", text: "#166534" },
  "Rejected - Needs Review": { bg: "#FEE2E2", text: "#991B1B" },
};

export default function TicketList() {
  const { unitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",         // free text (job number + restaurant global search)
    restaurant: "",     // per-column dropdown
    equipment_category: "",
    priority: "",
    status: "",
    assignee: "",       // per-column assignee contains-text
  });
  const [restaurants, setRestaurants] = useState([]);

  // Page size — "15 per page" by default or "all" (loads up to 2000 at once).
  const [pageSize, setPageSize] = useState(15);
  const limit = pageSize;

  useEffect(() => {
    fetchTickets();
  }, [unitId, page, filters]);

  // Live-drop cancelled/deleted tickets without needing a manual refresh.
  useEffect(() => {
    const onRemoved = (e) => {
      const id = e?.detail?.ticket_id;
      if (!id) { fetchTickets(); return; }
      setTickets(prev => prev.filter(t => t.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
    };
    window.addEventListener("efacilicare:ticket_cancelled", onRemoved);
    window.addEventListener("efacilicare:ticket_deleted", onRemoved);
    return () => {
      window.removeEventListener("efacilicare:ticket_cancelled", onRemoved);
      window.removeEventListener("efacilicare:ticket_deleted", onRemoved);
    };
  }, []);

  // Load restaurant list once per market for the Restaurant column dropdown.
  useEffect(() => {
    if (!unitId) return;
    (async () => {
      try {
        const res = await api.get(`/markets/${encodeURIComponent(unitId)}/restaurants`);
        setRestaurants(res.data.restaurants || []);
      } catch {
        setRestaurants([]);
      }
    })();
  }, [unitId]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("unit_id", unitId);
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.equipment_category) params.append("equipment_category", filters.equipment_category);
      if (filters.restaurant) params.append("restaurant", filters.restaurant);
      if (filters.assignee) params.append("assignee", filters.assignee);
      if (filters.search) params.append("search", filters.search);

      const res = await api.get(`/tickets?${params.toString()}`);
      setTickets(res.data.tickets);
      setTotal(res.data.total);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: "", restaurant: "", equipment_category: "", priority: "", status: "", assignee: "" });
    setPage(1);
  };

  const hasActiveColumnFilter =
    filters.restaurant ||
    filters.equipment_category ||
    filters.priority ||
    filters.status ||
    filters.assignee;

  const getStatusStyle = (status) => {
    const style = STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#6B7280" };
    return { backgroundColor: style.bg, color: style.text };
  };

  const getPriorityStyle = (priority) => {
    const styles = {
      P1: "priority-p1",
      P2: "priority-p2",
      P3: "priority-p3",
      P4: "priority-p4",
    };
    return styles[priority] || "priority-p4";
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={unitId} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">All Tickets</h1>
            <p className="text-gray-600 mt-1">{total} total tickets</p>
          </div>
          {canCreateTickets(user?.role) && (
            <Link to={`/new-ticket/${unitId}`}>
              <Button className="mt-4 sm:mt-0 bg-indigo-600 hover:bg-indigo-700" data-testid="new-ticket-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Ticket
              </Button>
            </Link>
          )}
        </div>

        {/* Global search + clear */}
        <Card className="mb-6 bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by job number or restaurant..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              {(filters.search || hasActiveColumnFilter) && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-gray-600"
                  data-testid="clear-filters-btn"
                >
                  <Filter className="w-4 h-4 mr-1.5" />
                  Clear all filters
                </Button>
              )}

              {/* Page-size toggle (15 per page ↔ See all) */}
              <div className="flex items-center gap-1 border border-gray-200 rounded-md overflow-hidden shrink-0">
                <button
                  type="button"
                  onClick={() => { setPageSize(15); setPage(1); }}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${pageSize === 15 ? "bg-indigo-600 text-white" : "text-gray-700 hover:bg-gray-50"}`}
                  data-testid="page-size-15-btn"
                >
                  15 / page
                </button>
                <button
                  type="button"
                  onClick={() => { setPageSize(2000); setPage(1); }}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${pageSize !== 15 ? "bg-indigo-600 text-white" : "text-gray-700 hover:bg-gray-50"}`}
                  data-testid="page-size-all-btn"
                  title="Load every ticket in a single scrollable page"
                >
                  See all
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets Table */}
        <Card className="bg-white border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : tickets.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full data-table">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-4">Job Number</th>
                        <th className="text-left py-3 px-4">Restaurant</th>
                        <th className="text-left py-3 px-4">Equipment</th>
                        <th className="text-left py-3 px-4">Priority</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4 hidden lg:table-cell">Created</th>
                        <th className="text-left py-3 px-4 hidden lg:table-cell">Assignee</th>
                      </tr>
                      {/* Excel-style per-column filter row */}
                      <tr className="border-b border-gray-200 bg-white">
                        <th className="py-2 px-3">
                          {/* Job-number uses the global search */}
                          <span className="text-[10px] uppercase tracking-wider text-gray-400">
                            Use search bar
                          </span>
                        </th>
                        <th className="py-2 px-3">
                          <Select
                            value={filters.restaurant || "__all__"}
                            onValueChange={(v) =>
                              handleFilterChange("restaurant", v === "__all__" ? "" : v)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="col-restaurant-filter">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All</SelectItem>
                              {restaurants.map((r) => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-3">
                          <Select
                            value={filters.equipment_category || "__all__"}
                            onValueChange={(v) =>
                              handleFilterChange("equipment_category", v === "__all__" ? "" : v)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="col-equipment-filter">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All</SelectItem>
                              {EQUIPMENT_CATEGORIES.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-3">
                          <Select
                            value={filters.priority || "__all__"}
                            onValueChange={(v) =>
                              handleFilterChange("priority", v === "__all__" ? "" : v)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="col-priority-filter">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All</SelectItem>
                              <SelectItem value="P1">P1 - Safety</SelectItem>
                              <SelectItem value="P2">P2 - Urgent</SelectItem>
                              <SelectItem value="P3">P3 - Not Urgent</SelectItem>
                              <SelectItem value="P4">P4 - Can Wait</SelectItem>
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-3">
                          <Select
                            value={filters.status || "__all__"}
                            onValueChange={(v) =>
                              handleFilterChange("status", v === "__all__" ? "" : v)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="col-status-filter">
                              <SelectValue placeholder="All" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">All</SelectItem>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-3 hidden lg:table-cell">
                          <span className="text-[10px] uppercase tracking-wider text-gray-400">—</span>
                        </th>
                        <th className="py-2 px-3 hidden lg:table-cell">
                          <Input
                            value={filters.assignee}
                            onChange={(e) => handleFilterChange("assignee", e.target.value)}
                            placeholder="Contains…"
                            className="h-8 text-xs"
                            data-testid="col-assignee-filter"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <tr 
                          key={ticket.id} 
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/ticket/${ticket.id}`)}
                          data-testid={`ticket-row-${ticket.id}`}
                        >
                          <td className="py-3 px-4 font-medium text-indigo-600">{ticket.job_number}</td>
                          <td className="py-3 px-4 text-gray-900">{ticket.restaurant_name}</td>
                          <td className="py-3 px-4 text-gray-600">{ticket.equipment_category}</td>
                          <td className="py-3 px-4">
                            {ticket.priority ? (
                              <Badge className={`${getPriorityStyle(ticket.priority)} text-xs`}>
                                {ticket.priority}
                              </Badge>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              className="text-xs whitespace-nowrap"
                              style={getStatusStyle(ticket.status)}
                            >
                              {ticket.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-sm hidden lg:table-cell">
                            {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                          </td>
                          <td className="py-3 px-4 text-gray-600 hidden lg:table-cell">
                            {ticket.assignee_name || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-gray-600">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <TicketIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No tickets found</h3>
                <p className="text-gray-500 mb-4">
                  {filters.search || filters.status || filters.priority || filters.equipment_category
                    ? "Try adjusting your filters"
                    : "Create your first ticket to get started"
                  }
                </p>
                {canCreateTickets(user?.role) && (
                  <Link to={`/new-ticket/${unitId}`}>
                    <Button className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Ticket
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
